import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import SEO from '../components/Common/SEO';
import { SparklesIcon, GlobeAltIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { teamMemberService } from '../services/teamMemberService';
import { partnerService } from '../services/partnerService';
import { apiService } from '../services/apiService';
import { TeamMember, Partner } from '../types';
import elephantHero from '../assets/elephant-hero.png';
import { API_BASE_URL } from '../config/apiConfig';

const API_BASE = API_BASE_URL.replace(/\/api$/, '');

/** Resolves any image URL (Cloudinary, local, or missing) to a displayable URL */
const resolveImg = (url?: string | null): string => {
  if (!url || url === '') return '';
  // If it's already a full URL (Cloudinary or absolute), return as is
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // If it's a relative path, prepend backend base
  return `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
};

const About: React.FC = () => {
  const { actualTheme } = useTheme();
  const { scrollY } = useScroll();
  
  // Parallax effects
  const bgY = useTransform(scrollY, [0, 1000], [0, 400]);
  const textY = useTransform(scrollY, [0, 500], [0, 100]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [safariCount, setSafariCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const isDark = actualTheme === 'dark';

  const [heritageDays, setHeritageDays] = useState<number>(0);
  const targetHeritageDays = Math.floor((Date.now() - new Date('2024-04-01T00:00:00').getTime()) / (1000 * 60 * 60 * 24));

  // Animate the counter on load
  useEffect(() => {
    let startTime: number | null = null;
    const duration = 1500; // 1.5 seconds animation

    const animateCount = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // Ease out cubic
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      setHeritageDays(Math.floor(easeProgress * targetHeritageDays));
      if (progress < 1) {
        requestAnimationFrame(animateCount);
      }
    };
    
    requestAnimationFrame(animateCount);

    // Keep it updated if user leaves tab open across midnight
    const interval = setInterval(() => {
       const newDiff = Math.floor((Date.now() - new Date('2024-04-01T00:00:00').getTime()) / (1000 * 60 * 60 * 24));
       setHeritageDays(newDiff);
    }, 60 * 60 * 1000); // Check every hour
    
    return () => clearInterval(interval);
  }, [targetHeritageDays]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [members, partnersData, tours] = await Promise.all([
          teamMemberService.getActiveTeamMembers(true),
          partnerService.getActivePartners(true),
          apiService.getAllTours(true)
        ]);
        setTeamMembers(members);
        setPartners(partnersData);
        setSafariCount(tours?.length || 0);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-900 text-slate-100' : 'bg-neutral-50 text-slate-900'} font-['Outfit'] selection:bg-blue-600 selection:text-white overflow-x-hidden`}>
      <SEO
        title="Our Story | SightTrail Safaris"
        description="Premium East African safari experiences curated by professional safari architects since 2024."
      />

      {/* Compact Floating Editorial Introduction */}
      <section className={`pt-8 pb-0 sm:pt-14 sm:pb-0 ${isDark ? 'bg-slate-950' : 'bg-neutral-50'} relative`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className={`relative z-10 p-6 sm:p-12 lg:p-16 rounded-[2rem] sm:rounded-[3.5rem] border shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] ${isDark ? 'bg-slate-900/40 border-slate-800/50 backdrop-blur-xl' : 'bg-white border-slate-100'}`}
          >
            {/* Inner content wrapper */}
            <div className="flex flex-col items-center text-center relative z-10">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="mb-6"
              >
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100/50 dark:border-blue-800/20">
                  <span className="text-blue-600 dark:text-blue-400 font-bold uppercase tracking-[0.2em] text-[10px]">
                    The Safari Standard
                  </span>
                </div>
              </motion.div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 dark:text-white leading-tight mb-6">
                About <span className="font-['Playfair_Display'] italic text-blue-600 font-medium whitespace-nowrap">SightTrails.</span>
              </h1>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="text-base sm:text-lg md:text-xl text-slate-500 dark:text-slate-400 max-w-2xl font-light leading-relaxed font-['Outfit']"
              >
                Building the future of African exploration, one extraordinary journey at a time.
              </motion.p>
            </div>
            
            {/* Minimal background glow */}
            <div className={`absolute top-0 right-0 w-48 h-48 ${isDark ? 'bg-blue-500/5' : 'bg-blue-50/30'} blur-[80px] -z-10 rounded-full`} />
          </motion.div>
        </div>
      </section>


      {/* Split Mission Section */}
      <section className={`pt-8 pb-20 sm:pt-12 sm:pb-32 lg:pt-16 lg:pb-48 px-4 sm:px-6 lg:px-8 relative overflow-hidden ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        {/* Subtle decorative elements */}
        <div className="absolute top-0 left-0 w-full h-px bg-slate-100 dark:bg-slate-800" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 sm:gap-20 lg:gap-32 items-center">
            <motion.div
               initial={{ opacity: 0, x: -40 }}
               whileInView={{ opacity: 1, x: 0 }}
               viewport={{ once: true }}
               transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
               className="relative z-10 text-center lg:text-left"
            >
              <div className="inline-flex items-center text-blue-600 dark:text-blue-400 font-black uppercase tracking-[0.4em] text-[8px] sm:text-[10px] mb-6 sm:mb-8 bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-full border border-blue-100 dark:border-blue-800/30 shadow-sm">
                The Architecture of Adventure
              </div>
              
              <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-8 sm:mb-10 tracking-tighter leading-[1.1] text-slate-800 dark:text-white">
                Curating <span className="text-blue-600">The Extraordinary</span>
              </h2>
              
              <div className="space-y-6 sm:space-y-8 text-base sm:text-lg lg:text-xl text-slate-500 dark:text-slate-400 leading-relaxed font-light">
                <p>
                  Founded in Nairobi in <span className="font-bold text-slate-800 dark:text-slate-200">April 2024</span>, SightTrail Safaris was conceived by <span className="italic font-serif text-blue-600">Saitoti Njapit</span>—a visionary with a profound enthusiasm for global travel and professional expedition management. 
                </p>
                <p>
                  Drawing from a rich heritage of East African exploration, we bridge the gap between world-class luxury and raw, untamed wilderness. 
                </p>
              </div>
              
              <div className="mt-10 sm:mt-14 grid grid-cols-2 gap-4 sm:gap-8 lg:gap-12 text-left">
                <div className="group p-4 sm:p-0 bg-slate-50 dark:bg-slate-800/50 sm:bg-transparent rounded-2xl">
                  <div className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-800 dark:text-white mb-2 sm:mb-3 tracking-tighter transition-all duration-300 group-hover:text-blue-600 group-hover:-translate-y-1">
                    {heritageDays}<span className="text-blue-500 font-serif">+</span>
                  </div>
                  <div className="text-[8px] sm:text-[10px] lg:text-xs font-black uppercase tracking-[0.2em] sm:tracking-[0.3em] text-slate-400 dark:text-slate-500 group-hover:text-blue-500/60 transition-colors">
                    Days of Continuity
                  </div>
                </div>
                <div className="group p-4 sm:p-0 bg-slate-50 dark:bg-slate-800/50 sm:bg-transparent rounded-2xl">
                  <div className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-800 dark:text-white mb-2 sm:mb-3 tracking-tighter transition-all duration-300 group-hover:text-blue-600 group-hover:-translate-y-1">
                    {safariCount}<span className="text-blue-500 font-serif">+</span>
                  </div>
                  <div className="text-[8px] sm:text-[10px] lg:text-xs font-black uppercase tracking-[0.2em] sm:tracking-[0.3em] text-slate-400 dark:text-slate-500 group-hover:text-blue-500/60 transition-colors">
                    Curated Expeditions
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.95, rotateY: -10 }}
              whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-[2rem] sm:rounded-[3rem] overflow-hidden aspect-square sm:aspect-video lg:aspect-[4/5] shadow-2xl group border-[6px] sm:border-8 border-white dark:border-slate-800"
            >
              <img 
                src={elephantHero} 
                alt="African Spirit" 
                className="w-full h-full object-cover transition-transform duration-[4s] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              
              {/* Glassmorphism over-image card */}
              <div className="absolute bottom-6 sm:bottom-10 left-6 sm:left-10 right-6 sm:right-10 backdrop-blur-xl bg-white/10 p-5 sm:p-8 rounded-[1.5rem] sm:rounded-[2rem] border border-white/20 text-white shadow-2xl">
                 <p className="text-[8px] sm:text-[10px] font-black uppercase tracking-[0.4em] text-blue-400 mb-2 sm:mb-3 drop-shadow-lg">Majestic Presence</p>
                 <h4 className="text-lg sm:text-2xl lg:text-3xl font-['Playfair_Display'] italic leading-tight drop-shadow-xl">
                    Witnessing greatness across the vast savannah.
                 </h4>
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Team / Visionaries Section */}
      <section className={`py-20 sm:py-32 ${isDark ? 'bg-slate-950/40' : 'bg-white'} relative overflow-hidden text-center lg:text-left`}>
        {/* Abstract Architectural Background Design */}
        <div className="absolute top-0 right-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-blue-600/5 rounded-full blur-[80px] sm:blur-[120px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-blue-600/5 rounded-full blur-[80px] sm:blur-[120px] translate-y-1/2 -translate-x-1/2" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 sm:mb-20 lg:mb-32">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl sm:text-6xl font-bold mb-4 sm:mb-6 tracking-tight text-slate-800 dark:text-white"
            >
              The Visionaries
            </motion.h2>
            <div className="w-16 sm:w-24 h-1 sm:h-1.5 bg-blue-600 mx-auto rounded-full mb-6 sm:mb-8" />
            <p className="text-base sm:text-xl text-slate-500 max-w-3xl mx-auto font-light leading-relaxed">
              Meet the architects of your next adventure—a team of experts dedicated to building journeys of extraordinary magnitude.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-slate-300 dark:bg-slate-800 rounded-3xl aspect-[3/4]" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 sm:gap-16 lg:gap-24">
              {teamMembers.map((member, i) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                  className="flex flex-col items-center text-center group"
                >
                  <div className={`relative w-48 h-48 sm:w-64 sm:h-64 rounded-full overflow-hidden mb-6 sm:mb-10 transition-all duration-700 ring-offset-4 ring-offset-transparent ring-0 group-hover:ring-4 ${isDark ? 'group-hover:ring-blue-500/30' : 'group-hover:ring-blue-600/20'}`}>
                    <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <img
                      src={resolveImg(member.imageUrl) || 'https://placehold.co/400x400'}
                      alt={member.name}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                    />
                  </div>
                  
                  <div className="space-y-3 sm:space-y-4">
                    <h3 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-800 dark:text-white group-hover:text-blue-500 transition-colors duration-300">
                      {member.name}
                    </h3>
                    
                    <div className="flex flex-col items-center gap-2 sm:gap-3">
                      <span className="text-[10px] sm:text-sm font-black uppercase tracking-[0.2em] sm:tracking-[0.3em] text-blue-600 dark:text-blue-400">
                        {member.role}
                      </span>
                      
                      {member.isFounder && (
                        <motion.div 
                          animate={{ y: [0, -4, 0] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                          className="inline-block bg-blue-600 text-white text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] px-3 sm:px-4 py-1 sm:py-1.5 rounded-full shadow-lg shadow-blue-600/20"
                        >
                          Founder & Visionary
                        </motion.div>
                      )}
                    </div>

                    {/* Bio removed for cleaner aesthetic */}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Strategic Partners Section */}
      <section className={`py-20 sm:py-32 ${isDark ? 'bg-slate-900' : 'bg-neutral-50'} relative overflow-hidden border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 sm:mb-20 lg:mb-32">
            <h2 className="text-3xl sm:text-5xl font-bold mb-4 sm:mb-6 tracking-tight text-slate-800 dark:text-white">Our Strategic Alliance</h2>
            <div className="w-16 sm:w-20 h-1 sm:h-1.5 bg-blue-600 mx-auto rounded-full mb-6 sm:mb-8" />
            <p className="text-base sm:text-xl text-slate-500 max-w-3xl mx-auto font-light leading-relaxed">
              We collaborate with the continent's most prestigious entities to ensure your journey maintains an unparalleled standard of safety, luxury, and authenticity.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="animate-pulse bg-slate-300 dark:bg-slate-800 rounded-2xl h-32" />
              ))}
            </div>
          ) : partners.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-10 lg:gap-12">
              {partners.map((partner, i) => {
                const CardContent = (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                    className={`h-full p-6 sm:p-10 rounded-[1.5rem] sm:rounded-[2.5rem] flex flex-col items-center justify-center text-center transition-all duration-500 hover:shadow-2xl border ${isDark ? 'bg-slate-800/40 border-slate-700/50 hover:bg-slate-800 hover:border-blue-500/30' : 'bg-white border-slate-100 hover:border-blue-600/20'} group ${partner.website ? 'cursor-pointer' : ''}`}
                  >
                    <div className="w-16 h-16 sm:w-28 sm:h-28 relative mb-4 sm:mb-8 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                      {partner.logoUrl ? (
                        <img 
                          src={resolveImg(partner.logoUrl)} 
                          alt={partner.name} 
                          className="max-w-full max-h-full object-contain filter grayscale-0 md:grayscale md:group-hover:grayscale-0 transition-all duration-700 brightness-100 group-hover:brightness-110"
                        />
                      ) : partner.imageUrl ? (
                        <img 
                          src={resolveImg(partner.imageUrl)} 
                          alt={partner.name} 
                          className="w-full h-full object-cover rounded-full grayscale-0 md:grayscale md:group-hover:grayscale-0 transition-all duration-700"
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center border-2 border-dashed border-slate-200 dark:border-slate-700">
                          <GlobeAltIcon className="w-8 h-8 sm:w-12 sm:h-12 text-slate-300 dark:text-slate-600" />
                        </div>
                      )}
                    </div>
                    <h3 className="text-sm sm:text-xl font-bold mb-1 sm:mb-2 text-slate-800 dark:text-white group-hover:text-blue-500 transition-colors duration-300">{partner.name}</h3>
                    {partner.type && (
                      <span className="text-[8px] sm:text-[10px] text-blue-600 dark:text-blue-400 uppercase tracking-[0.1em] sm:tracking-[0.2em] font-black px-2 sm:px-3 py-1 bg-blue-50 dark:bg-blue-900/30 rounded-full">
                        {partner.type}
                      </span>
                    )}
                    {partner.description && (
                      <p className="hidden sm:block text-xs text-slate-500 dark:text-slate-400 mt-4 leading-relaxed font-light opacity-80 line-clamp-2 italic">
                        "{partner.description}"
                      </p>
                    )}
                  </motion.div>
                );

                return partner.website ? (
                  <a 
                    key={partner.id} 
                    href={partner.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block h-full outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-[1.5rem] sm:rounded-[2.5rem]"
                  >
                    {CardContent}
                  </a>
                ) : (
                  <div key={partner.id} className="h-full">
                    {CardContent}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20 bg-slate-100/50 dark:bg-slate-800/30 rounded-[2rem] sm:rounded-[3rem] border border-dashed border-slate-200 dark:border-slate-700">
              <p className="text-slate-400 font-light italic tracking-wide">Our strategic partner network is currently being expanded.</p>
            </div>
          )}
        </div>
      </section>



      {/* Minimalist Call To Action Strip */}
      <section className={`py-12 sm:py-16 ${isDark ? 'bg-slate-900 border-t border-slate-800' : 'bg-white border-t border-slate-100'} relative overflow-hidden`}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
            <div className="max-w-2xl">
              <h2 className="text-2xl sm:text-3xl font-bold mb-3 tracking-tight text-slate-800 dark:text-white">
                Curate <span className="font-['Playfair_Display'] italic text-blue-600">the Wild.</span>
              </h2>
              <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 font-light leading-relaxed">
                Ready to begin your transition into the heart of the African wilderness? Connect with our experts today.
              </p>
            </div>
            <a
              href="/tours"
              className="inline-flex items-center px-10 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full text-xs font-bold uppercase tracking-[0.2em] transition-all hover:scale-105 shadow-xl hover:shadow-blue-600/20 active:scale-95 whitespace-nowrap"
            >
              Start Your Journey
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;